import React, { useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Link, CircularProgress, FormControlLabel, Switch,
  Box, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';

import { getCodingFlowDashboard } from '../../api';
import useFetch from '../../hooks/useFetch';

const columnStyles = {
  existing_codes: { backgroundColor: '#e0f7fa' },
  existing_unrefreshed_codes: { backgroundColor: '#f0faff' },
  new_codes: { backgroundColor: '#e8f5e9' },
  doctor_codes: { backgroundColor: '#f1fdf2' },
  coder_codes: { backgroundColor: '#f1fdf2' },
  resolved: { backgroundColor: '#abe3ecff' },
  accepted_codes: { backgroundColor: '#f0faff' },
  dismissed_codes: { backgroundColor: '#ffecec' },
  unattended_codes: { backgroundColor: '#fff9e6' },
  followup_codes: { backgroundColor: '#ebe3f3' },
  accepted_recapture: { backgroundColor: '#f0faff' },
  dismissed_recapture: { backgroundColor: '#ffecec' },
  unattended_recapture: { backgroundColor: '#fff9e6' },
  followup_recapture: { backgroundColor: '#ebe3f3' },
  accepted_allymar: { backgroundColor: '#f0faff' },
  dismissed_allymar: { backgroundColor: '#ffecec' },
  unattended_allymar: { backgroundColor: '#fff9e6' },
  followup_allymar: { backgroundColor: '#ebe3f3' }
};

function blendColors(c1, c2, weight = 0.5) {
  const hexToRgb = (hex) => {
    const parsed = hex.replace('#', '');
    return [
      parseInt(parsed.substring(0, 2), 16),
      parseInt(parsed.substring(2, 4), 16),
      parseInt(parsed.substring(4, 6), 16)
    ];
  };
  const rgbToHex = (rgb) => {
    return (
      '#' +
      rgb
        .map((x) => {
          const hex = Math.round(x).toString(16);
          return hex.length === 1 ? '0' + hex : hex;
        })
        .join('')
    );
  };
  const r1 = hexToRgb(c1);
  const r2 = hexToRgb(c2);
  const blended = r1.map((v, i) => v * weight + r2[i] * (1 - weight));
  return rgbToHex(blended);
}

const Dashboard = ({ username, token }) => {
  const [days, setDays] = useState(14);
  const [selectedOption, setSelectedOption] = useState(14); // Track the selected dropdown option
  const [params, setParams] = useState({ username, token, days });
  const [normalize, setNormalize] = useState(false);
  const [isChangingDays, setIsChangingDays] = useState(false);

  useEffect(() => {
    setParams({ username, token, days });
  }, [days, username, token]);

  const { data, loading, error } = useFetch(getCodingFlowDashboard, params);

  // Clear the changing days loading state when data loads
  useEffect(() => {
    if (!loading && data) {
      setIsChangingDays(false);
    }
  }, [loading, data]);

  // Show loading when either the hook is loading OR we're changing days
  const showLoading = loading || isChangingDays;

  if (error) return <div>Error: {error.message}</div>;

  const grouped = data ? data.reduce((acc, row) => {
    acc[row.department] = acc[row.department] || [];
    acc[row.department].push(row);
    return acc;
  }, {}) : {};
      
  // Helper to get days since start of year
  const getDaysSinceStartOfYear = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const diff = Math.floor((now - start) / (1000 * 60 * 60 * 24)) + 1;
    return diff;
  };

  const grandTotal = {
    patients_visits: 0,
    visits_with_new_codes: 0,
    visits_without_new_codes: 0,
    new_codes: 0,
    existing_codes: 0,
    existing_unrefreshed_codes: 0,
    doctor_codes: 0,
    coder_codes: 0,
    resolved: 0,
    accepted_codes: 0,
    dismissed_codes: 0,
    unattended_codes: 0,
    followup_codes: 0,
    accepted_recapture: 0,
    dismissed_recapture: 0,
    unattended_recapture: 0,
    followup_recapture: 0,
    accepted_allymar: 0,
    dismissed_allymar: 0,
    unattended_allymar: 0,
    followup_allymar: 0
  };

  if (data) {
    data.forEach((row) => {
      Object.keys(grandTotal).forEach((key) => {
        grandTotal[key] += row[key];
      });
    });
  }

  const formatMetric = (value, visits) => {
    return normalize && visits > 0 ? ((value / visits) * 1000).toFixed(0) : value;
  };

  const metricFields = [
    'patients_visits',
    'visits_with_new_codes',
    'visits_without_new_codes',
    'existing_codes',
    'existing_unrefreshed_codes',
    'new_codes',
    'doctor_codes',
    'coder_codes',
    'resolved',
    'accepted_codes',
    'dismissed_codes',
    'unattended_codes',
    'followup_codes',
    'accepted_recapture',
    'dismissed_recapture',
    'unattended_recapture',
    'followup_recapture',
    'accepted_allymar',
    'dismissed_allymar',
    'unattended_allymar',
    'followup_allymar'
  ];

  return (
    <Box height="87vh" display="flex" flexDirection="column" position="relative">
      {/* Loading Overlay */}
      {showLoading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(255, 255, 255, 0.8)"
          zIndex={9999}
        >
          <Box display="flex" flexDirection="column" alignItems="center">
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2, color: '#666' }}>
              Loading dashboard data...
            </Typography>
          </Box>
        </Box>
      )}
      
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2, px: 2, pt: 2 }}>
        <Typography variant="h5">HCC Summary</Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={<Switch checked={normalize} onChange={() => setNormalize(!normalize)} />}
            label="Normalize per 1000 Visits"
          />
          <FormControl size="small">
            <InputLabel id="day-range-label">Days</InputLabel>
            <Select
              labelId="day-range-label"
              value={selectedOption}
              label="Days"
              onChange={(e) => {
                setIsChangingDays(true);
                setSelectedOption(e.target.value);
                if (e.target.value === 'YTD') {
                  setDays(getDaysSinceStartOfYear());
                } else {
                  setDays(Number(e.target.value));
                }
              }}
            >
              <MenuItem value={14}>Last 14 Days</MenuItem>
              <MenuItem value={30}>Last 30 Days</MenuItem>
              <MenuItem value={90}>Last 90 Days</MenuItem>
              <MenuItem value={'YTD'}>YTD</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ flexGrow: 1 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#fff',
                  top: 0,
                  zIndex: 2,
                  position: 'sticky',
                  left: 0,
                  minWidth: 240,
                  boxShadow: '2px 0 2px -1px #e0e0e0'
                }}
              >
                Department & Provider
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fff', top: 0, zIndex: 1 }}>Patient Visits</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fff', top: 0, zIndex: 1 }}>With New Codes</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fff', top: 0, zIndex: 1 }}>Without New Codes</TableCell>
              <TableCell sx={{ fontWeight: 'bold', ...columnStyles.existing_codes, top: 0, zIndex: 1 }}>Existing Codes</TableCell>
              <TableCell sx={{ fontWeight: 'bold', ...columnStyles.existing_unrefreshed_codes, top: 0, zIndex: 1 }}>Unrefreshed</TableCell>
              <TableCell sx={{ fontWeight: 'bold', ...columnStyles.new_codes, top: 0, zIndex: 1 }}>New Codes</TableCell>
              <TableCell sx={{ fontWeight: 'bold', ...columnStyles.doctor_codes, top: 0, zIndex: 1 }}>Doctor</TableCell>
              <TableCell sx={{ fontWeight: 'bold', ...columnStyles.coder_codes, top: 0, zIndex: 1 }}>Coder</TableCell>
              <TableCell sx={{ fontWeight: 'bold', ...columnStyles.resolved, top: 0, zIndex: 1 }}>Resolved</TableCell>
              <TableCell sx={{ fontWeight: 'bold', ...columnStyles.accepted_codes, top: 0, zIndex: 1 }}>Accepted (unassessed)</TableCell>
              <TableCell sx={{ fontWeight: 'bold', ...columnStyles.dismissed_codes, top: 0, zIndex: 1 }}>Dismissed</TableCell>
              <TableCell sx={{ fontWeight: 'bold', ...columnStyles.unattended_codes, top: 0, zIndex: 1 }}>Unattended</TableCell>
              <TableCell sx={{ fontWeight: 'bold', ...columnStyles.followup_codes, top: 0, zIndex: 1 }}>Follow Up</TableCell>
              <TableCell sx={{ fontWeight: 'bold', ...columnStyles.accepted_recapture, top: 0, zIndex: 1 }}>Accepted Recapture (unassessed)</TableCell>
              <TableCell sx={{ fontWeight: 'bold', ...columnStyles.dismissed_recapture, top: 0, zIndex: 1 }}>Dismissed Recapture</TableCell>
              <TableCell sx={{ fontWeight: 'bold', ...columnStyles.unattended_recapture, top: 0, zIndex: 1 }}>Unattended Recapture</TableCell>
              <TableCell sx={{ fontWeight: 'bold', ...columnStyles.followup_recapture, top: 0, zIndex: 1 }}>Follow Up Recapture</TableCell>
              <TableCell sx={{ fontWeight: 'bold', ...columnStyles.accepted_allymar, top: 0, zIndex: 1 }}>Accepted Allymar (unassessed)</TableCell>
              <TableCell sx={{ fontWeight: 'bold', ...columnStyles.dismissed_allymar, top: 0, zIndex: 1 }}>Dismissed Allymar</TableCell>
              <TableCell sx={{ fontWeight: 'bold', ...columnStyles.unattended_allymar, top: 0, zIndex: 1 }}>Unattended Allymar</TableCell>
              <TableCell sx={{ fontWeight: 'bold', ...columnStyles.followup_allymar, top: 0, zIndex: 1 }}>Follow Up Allymar</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0',
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                  boxShadow: '2px 0 2px -1px #e0e0e0'
                }}
              >
                <Typography variant="h6">Grand Total</Typography>
              </TableCell>
              {metricFields.map((key) => (
                <TableCell
                  key={key}
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: columnStyles[key]?.backgroundColor
                      ? blendColors(columnStyles[key].backgroundColor, '#e0e0e0')
                      : '#e0e0e0'
                  }}
                >
                  {key === 'patients_visits'
                    ? grandTotal[key]
                    : formatMetric(grandTotal[key], grandTotal['patients_visits'])}
                </TableCell>
              ))}
            </TableRow>

            {Object.entries(grouped).map(([department, providers]) => {
              const totals = Object.fromEntries(metricFields.map((key) => [key, 0]));

              providers.forEach((p) => {
                metricFields.forEach((key) => {
                  totals[key] += p[key];
                });
              });

              return (
                <React.Fragment key={department}>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: '#f5f5f5',
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        boxShadow: '2px 0 2px -1px #e0e0e0'
                      }}
                    >
                      <Typography variant="h6">{department}</Typography>
                    </TableCell>

                    {metricFields.map((key) => (
                      <TableCell
                        key={key}
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor: columnStyles[key]?.backgroundColor
                            ? blendColors(columnStyles[key].backgroundColor, '#f5f5f5')
                            : '#f5f5f5'
                        }}
                      >
                        {key === 'patients_visits'
                          ? totals[key]
                          : formatMetric(totals[key], totals['patients_visits'])}
                      </TableCell>
                    ))}
                  </TableRow>

                  {providers.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell
                        sx={{
                          backgroundColor: '#fff',
                          position: 'sticky',
                          left: 0,
                          zIndex: 1,
                          boxShadow: '2px 0 2px -1px #e0e0e0'
                        }}
                      >
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{row.provider}
                      </TableCell>
                      {metricFields.map((field) => (
                        <TableCell key={field} sx={columnStyles[field] || {}}>
                          {field === 'patients_visits' ? (
                            row[field]
                          ) : (
                          Number(days) >= 14 ? (
                              <Link
                                underline="none"
                                href={`/coding_flow/visitdetails?department=${encodeURIComponent(
                                  row.department
                                )}&provider=${encodeURIComponent(row.provider)}&metric=${field}&days=${days}`}
                                target="_blank"
                              >
                                {formatMetric(row[field], row['patients_visits'])}
                              </Link>
                            ) : (
                              formatMetric(row[field], row['patients_visits'])
                            )
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Dashboard;